import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaClock, 
  FaUsers, 
  FaEnvelope,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle
} from 'react-icons/fa';
import { projectsData } from '../data/projectsData_new';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { id } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const project = projectsData.find(p => p.id === parseInt(id));

  // Force scroll to top on mount / id change to avoid flash of previous scroll position
  useEffect(() => {
    const scrollToTop = () => {
      try {
        window.scrollTo({ top: 0, left: 0 });
      } catch (e) {
        window.scrollTo(0, 0);
      }
      // also ensure document/body are at top (cover edge cases)
      try { document.documentElement.scrollTop = 0; } catch {};
      try { document.body.scrollTop = 0; } catch {};
    };

    // immediate
    scrollToTop();
    // after paint
    const raf = requestAnimationFrame(scrollToTop);
    // fallback shortly after
    const t = setTimeout(scrollToTop, 50);

    return () => {
      try { cancelAnimationFrame(raf); } catch {}
      clearTimeout(t);
    };
  }, [id]);

  if (!project) {
    return (
      <div className="project-not-found">
        <div className="container">
          <h2>Project Not Found</h2>
          <p>The project you're looking for doesn't exist.</p>
          <Link to="/" className="btn btn-primary">
            <FaArrowLeft /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project.gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.gallery.length - 1 : prev - 1
    );
  };

  return (
    <div className="project-detail">
      {/* Hero Section */}
      <div className="project-hero">
        <div className="container">
          <Link to="/" className="back-button">
            <FaArrowLeft /> Back to Projects
          </Link>
          <div className="project-hero-content">
            <div className="project-hero-text">
              <div className="project-categories">
                {project.technologies.map((tech, index) => (
                  <span key={index} className="category-tag">{tech}</span>
                ))}
              </div>
              <h1 className="project-hero-title">{project.title}</h1>
              <p className="project-hero-description">{project.shortDescription}</p>
              
              <div className="project-quick-info">
                <div className="info-item">
                  <FaCalendarAlt />
                  <span>{project.date}</span>
                </div>
                <div className="info-item">
                  <FaMapMarkerAlt />
                  <span>{project.location}</span>
                </div>
                <div className="info-item">
                  <FaClock />
                  <span>{project.duration}</span>
                </div>
                <div className="info-item">
                  <FaUsers />
                  <span>{project.participants}</span>
                </div>
              </div>
            </div>
            
            <div className="project-hero-image">
              <img src={project.image} alt={project.title} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="project-content">
        <div className="container">
          <div className="content-grid">
            
            {/* Left Column - Main Content */}
            <div className="main-content">
              
              {/* Detailed Description */}
              <section className="content-section">
                <h2>About {project.title}</h2>
                <div className="detailed-description">
                  {project.detailedDescription.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph.trim()}</p>
                  ))}
                </div>
              </section>

              {/* Image Gallery */}
              <section className="content-section">
                <h2>Event Gallery</h2>
                <div className="image-gallery">
                  <div className="gallery-main">
                    <img 
                      src={project.gallery[currentImageIndex]} 
                      alt={`${project.title} - Gallery ${currentImageIndex + 1}`}
                      className="gallery-main-image"
                    />
                    {project.gallery.length > 1 && (
                      <>
                        <button className="gallery-nav prev" onClick={prevImage}>
                          <FaChevronLeft />
                        </button>
                        <button className="gallery-nav next" onClick={nextImage}>
                          <FaChevronRight />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {project.gallery.length > 1 && (
                    <div className="gallery-thumbnails">
                      {project.gallery.map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(index)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* Highlights */}
              <section className="content-section">
                <h2>Event Highlights</h2>
                <div className="highlights-grid">
                  {project.highlights.map((highlight, index) => (
                    <div key={index} className="highlight-item">
                      <div className="highlight-icon">
                        <FaCheckCircle />
                      </div>
                      <p>{highlight}</p>
                    </div>
                  ))}
                </div>
              </section>

            </div>

            {/* Right Column - Sidebar */}
            <div className="sidebar">
              
              {/* Event Details */}
              <div className="sidebar-section">
                <h3>Event Details</h3>
                <div className="event-details">
                  <div className="detail-item">
                    <strong>Organizers:</strong>
                    <p>{project.organizers}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Contact:</strong>
                    <p>
                      <FaEnvelope />
                      <a href={`mailto:${project.contact}`}>{project.contact}</a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Sponsors */}
              <div className="sidebar-section">
                <h3>Our Sponsors</h3>
                <div className="sponsors-grid">
                  {project.sponsors.map((sponsor, index) => (
                    <div key={index} className="sponsor-item">
                      <img src={sponsor.logo} alt={sponsor.name} />
                      <p>{sponsor.name}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technologies/Categories */}
              <div className="sidebar-section">
                <h3>Categories</h3>
                <div className="technologies-list">
                  {project.technologies.map((tech, index) => (
                    <span key={index} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;